import { useEffect } from "react";
import { useStore } from "zustand";
import { uiStore } from "@/lib/stores/ui-store";
import { ws } from "@/lib/websocket-wrapper";

/**
 * The last mic and camera state reported to the server, for this tab.
 *
 * Deliberately module-level rather than a `useRef` (TECH-3157). The only caller,
 * `CombinedVoiceBroadcastView`, is a grid tile that UNMOUNTS on leaving a call,
 * verified in the browser, where the grid then reads "No items on grid". A ref
 * therefore died with the component and was `null` again on the next join, so
 * the equality guard below could never fire and every room join re-sent an
 * identical state. On the server that redundant update pulled the whole pending
 * presence flush into the 200 ms window.
 *
 * Module scope is the right lifetime. It matches the server's per-connection
 * `conn.isMicrophoneEnabled`, which only this hook's procedure ever writes, and
 * it resets on a full page reload, where the socket is new and the state does
 * need reporting again.
 */
let lastReportedStatus: {
	isMicrophoneEnabled: boolean;
	isCameraEnabled: boolean;
} | null = null;

/** Test seam: reset the module-level record between cases. */
export const __test__resetMediaStatusRecord = (): void => {
	lastReportedStatus = null;
};

export function useMediaStatusSync() {
	const room = useStore(uiStore, (state) => state.room);

	useEffect(() => {
		if (!room) {
			// No reset here. The record must survive leaving a room, or the next join
			// has nothing to compare against.
			return;
		}

		const sendMediaStatus = () => {
			const isMicrophoneEnabled =
				room.localParticipant?.isMicrophoneEnabled ?? false;
			const isCameraEnabled = room.localParticipant?.isCameraEnabled ?? false;

			const last = lastReportedStatus;
			if (
				last &&
				last.isMicrophoneEnabled === isMicrophoneEnabled &&
				last.isCameraEnabled === isCameraEnabled
			) {
				return;
			}

			lastReportedStatus = { isMicrophoneEnabled, isCameraEnabled };

			ws.send(
				JSON.stringify({
					id: crypto.randomUUID(),
					procedure: "users.updateMediaStatus",
					data: { isMicrophoneEnabled, isCameraEnabled },
				})
			);
		};

		sendMediaStatus();

		const localParticipant = room.localParticipant;
		localParticipant.on("trackMuted", sendMediaStatus);
		localParticipant.on("trackUnmuted", sendMediaStatus);
		localParticipant.on("localTrackPublished", sendMediaStatus);
		localParticipant.on("localTrackUnpublished", sendMediaStatus);

		return () => {
			localParticipant.off("trackMuted", sendMediaStatus);
			localParticipant.off("trackUnmuted", sendMediaStatus);
			localParticipant.off("localTrackPublished", sendMediaStatus);
			localParticipant.off("localTrackUnpublished", sendMediaStatus);
		};
	}, [room]);
}
